import {
  AfterViewInit,
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import {BehaviorSubject, Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import * as Moment from 'moment';
import {DxAccordionComponent} from "devextreme-angular";
import {AbstractControl, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {DateMoreThan} from "../../../../../shared/custom-validations/date-validations";
import {OrderPriority, OrderStatus} from '../../../results/models/order';
import {SelectionChangedEvent} from "devextreme/ui/accordion";
import {ResultEntrySearchFilter} from "../../models/SearchFilter";
import {camelCase, upperFirst} from "lodash";
import {Client} from "../../../../shared/models/order-entry/client";

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-search-panel]',
  templateUrl: './search-panel.component.html',
  styleUrls: ['./search-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchPanelComponent implements AfterViewInit, OnDestroy {
  @ViewChild(DxAccordionComponent, {static: false}) public accordion?: DxAccordionComponent;
  @Output() public formSubmitted = new EventEmitter<ResultEntrySearchFilter>();

  public statusEnum: OrderStatus = OrderStatus.All;
  public title: string = "Standard Result Entry";
  public collapsed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public form: FormGroup;
  public $destroy: Subject<void> = new Subject<void>();

  constructor(private formBuilder: FormBuilder, private ref: ChangeDetectorRef) {
    this.form = this.formBuilder.group({
      start: [Moment().format('MM/DD/YYYY'), [Validators.required]],
      end: [Moment().format('MM/DD/YYYY'), [Validators.required, DateMoreThan('start')]],
      clients: [[]],
      statuses: [[], [Validators.required]],
      priorities: [[OrderStatus[OrderStatus.Pending].toUpperCase()]]
    });
  }

  public search() {
    if (this.form?.valid) {
      this.submit();
      this.collapsed.next(true);
    }
  }

  public clear() {
    this.form?.get('start')?.setValue(null);
    this.form?.get('end')?.setValue(null);
    this.form?.get('clients')?.setValue([]);
    this.form?.get('statuses')?.setValue([OrderStatus[OrderStatus.Pending].toUpperCase()]);
    this.form?.get('priorities')?.setValue([OrderStatus[OrderStatus.Pending].toUpperCase()]);
    this.submit();
  }

  public collapsibleChange(event: SelectionChangedEvent) {
    if (event.addedItems.length > 0) {
      this.collapsed.next(false);
    } else {
      this.collapsed.next(true);
    }
  }

  public convertToFormControl(absCtrl?: AbstractControl | null): FormControl {
    return absCtrl as FormControl;
  }

  private submit() {
    if (!this.form?.get('start')?.value) {
      this.form?.get('start')?.setValue(Moment().format('MM/DD/YYYY'));
    }

    if (!this.form?.get('end')?.value) {
      this.form?.get('end')?.setValue(Moment().format('MM/DD/YYYY'));
    }

    this.formSubmitted.emit({
      ...this.form.value,
      clients: this.form.value.clients.map((c: number | Client) => typeof c === 'number' ? c : c.id),
      priorities: this.form.value.priorities.map((p: string) => OrderPriority[upperFirst(camelCase(p)) as keyof typeof OrderPriority]),
      statuses: this.form.value.statuses.map((s: string) => OrderStatus[upperFirst(camelCase(s)) as keyof typeof OrderStatus]),
    });
    this.ref.detectChanges();
  }

  ngAfterViewInit(): void {
    this.collapsed
      .pipe(takeUntil(this.$destroy))
      .subscribe(isCollapsed => {
        if (!this.accordion) return;
        if (isCollapsed) {
          this.accordion.instance.collapseItem(0);
        } else {
          this.accordion.instance.expandItem(0);
        }
      });
  }

  ngOnDestroy(): void {
    this.$destroy.next();
    this.$destroy.complete();
  }
}
